'use client';

import { trackCardUpClick } from '@libs/analytics';
import { usePathname } from 'next/navigation';
import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import { clsx, usePageLayout } from '@libs/common-next';
import dynamic from 'next/dynamic';
import type { FC } from 'react';

import type { CardUpVariantType } from '../../types';
import CardUpVariant from './CardUpVariant';
import type { CardUpBrandProps, CardOfferProps, CardUpProps, Card } from './types';
import { ColoredTextLink } from '../ColoredTextLink';

import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';

const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
    ssr: false,
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
    ssr: false,
});

const CardOfferVariant = dynamic(() => import('./CardOfferVariant'), {
    ssr: false,
});

const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
    '2up': props => <CardUpVariant sliceMax={2} {...props} />,
    '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...(props as unknown as CardUpBrandProps)} />,
    '3up': props => <CardUpVariant sliceMax={3} {...props} />,
    '4up': props => <CardUpVariant sliceMax={4} {...props} />,
    '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
    '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
    Info: props => <CardUpVariant sliceMax={4} {...props} />,
    '2up Offer': props => (
        <CardOfferVariant variant="2up" cards={[]} sliceMax={2} {...(props as unknown as CardOfferProps)} />
    ),
};

const validateCardTypes = (cards: Card[]): boolean => {
    const articleModel = cards.some(card => card.previewDescription);
    const articleModelsAll = cards.every(card => card.previewDescription);
    return articleModel && !articleModelsAll;
};

export default function CardUp({
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1,
}: CardUpProps) {
    const pathname = usePathname();
    const { flags } = usePageLayout();
    const { redesignedComponentsEnabled } = flags;

    if (cards?.length === 0) {
        return null;
    }

    const Component = ComponentCardUp[variant];

    if (!Component) {
        return null;
    }

    if (variant === '3up Article' && validateCardTypes(cards)) {
        return null;
    }

    const { link, actionText } = linkAction || {};

    const titleAfter =
        header && link && actionText ? (
            <ColoredTextLink
                data-testid="link-action"
                onClick={() => {
                    trackCardUpClick({
                        variant,
                        title: header,
                        cardName: actionText,
                        cardPosition: -1,
                    });
                }}
                href={link?.webUrl}
                color={linkActionColor}
            >
                {actionText}
            </ColoredTextLink>
        ) : null;

    const variantName = variant.toLowerCase().replaceAll(' ', '-');

    const noHeaderClass = !header ? `${variantName}--no-header` : '';

    const restyledVariants = ['2up', '3up', '3up Article', '4up', 'Info', '4Stack'];

    const isRestyled = restyledVariants.includes(variant) && Boolean(redesignedComponentsEnabled);

    const componentClassName = clsx(`card-up`, variantName, noHeaderClass, isRestyled && `card-up--restyled`);

    return (
        <LayoutContainer className={`card-up-container card-up-container--${variantName}`}>
            <Section
                data-testid="card-up"
                data-variant={variantName}
                className={componentClassName}
                title={header}
                description={description}
                titleAfter={titleAfter}
                style={{ backgroundColor }}
                headingTagName={isH1 ? 'h1' : 'h2'}
                {...(isRestyled && {
                    align: 'center',
                    titleAfterLocation: 'below-description',
                })}
            >
                <Component
                    header={header}
                    description={description}
                    linkAction={linkAction}
                    cards={cards}
                    variant={variant}
                    pathname={pathname ?? ''}
                />
            </Section>
        </LayoutContainer>
    );
}
